import { useEffect, useRef, useState } from "react";
import { api, cld, errMsg } from "../lib/api";
import { AdminHead } from "./AdminLayout";

const MAX_EDGE = 2800;

async function shrinkImage(file) {
  let bitmap;
  try {
    bitmap = await createImageBitmap(file);
  } catch (e) {
    const err = new Error("unsupported");
    err.shrinkError = "Could not read this image (HEIC files may need converting to JPG first).";
    throw err;
  }
  const scale = Math.min(1, MAX_EDGE / Math.max(bitmap.width, bitmap.height));
  const w = Math.round(bitmap.width * scale);
  const h = Math.round(bitmap.height * scale);
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  canvas.getContext("2d").drawImage(bitmap, 0, 0, w, h);
  bitmap.close?.();
  const blob = await new Promise((res) => canvas.toBlob(res, "image/jpeg", 0.9));
  if (!blob) {
    const err = new Error("encode failed");
    err.shrinkError = "Could not process this image.";
    throw err;
  }
  return blob;
}

export default function PhotosAdmin() {
  const [photos, setPhotos] = useState([]);
  const [studio, setStudio] = useState("balboa");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [errors, setErrors] = useState([]);
  const [progress, setProgress] = useState(null);
  const dragId = useRef(null);
  const fileRef = useRef(null);

  const load = () => api.get("/admin/photos").then((r) => setPhotos(r.data || [])).catch((e) => setErr(errMsg(e)));
  useEffect(() => {
    load();
  }, []);

  const upload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setErrors([]);
    setBusy(true);
    const failed = [];
    try {
    for (let i = 0; i < files.length; i++) {
      const f = files[i];
      setProgress({ index: i + 1, total: files.length, pct: 0, name: f.name });
      try {
        const blob = await shrinkImage(f);
        const fd = new FormData();
        fd.append("file", blob, f.name.replace(/\.[^.]+$/, "") + ".jpg");
        fd.append("studio", studio);
        await api.post("/admin/photos/upload", fd, {
          headers: { "Content-Type": "multipart/form-data" },
          timeout: 120000,
          onUploadProgress: (evt) => {
            if (!evt.total) return;
            setProgress({ index: i + 1, total: files.length, pct: Math.round((evt.loaded / evt.total) * 100), name: f.name });
          },
        });
      } catch (e2) {
        failed.push(`${f.name} — ${e2?.shrinkError || errMsg(e2)}`);
      }
    }
    } finally {
      setBusy(false);
      setProgress(null);
    }
    setErrors(failed);
    if (fileRef.current) fileRef.current.value = "";
    load();
  };

  const toggle = async (p) => {
    await api.patch(`/admin/photos/${p.id}`, { visible: !p.visible });
    load();
  };

  const setStudioFor = async (p) => {
    await api.patch(`/admin/photos/${p.id}`, { studio: p.studio === "bungalow" ? "balboa" : "bungalow" });
    load();
  };

  const remove = async (p) => {
    if (!window.confirm("Delete this photo from the site and Cloudinary?")) return;
    await api.delete(`/admin/photos/${p.id}`);
    load();
  };

  const onDrop = async (targetId) => {
    const from = photos.findIndex((p) => p.id === dragId.current);
    const to = photos.findIndex((p) => p.id === targetId);
    if (from < 0 || to < 0 || from === to) return;
    const next = [...photos];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    setPhotos(next);
    await api.put("/admin/photos/reorder", { ids: next.map((p) => p.id) });
  };

  return (
    <div data-testid="admin-photos">
      <AdminHead title="Photos" sub={`${photos.length} total · drag to reorder`} />

      <div className="card" style={{ marginBottom: 32 }}>
        <p className="label" style={{ marginBottom: 12 }}>Upload — JPG, PNG or WEBP (large photos are resized automatically)</p>
        <label className="field" style={{ maxWidth: 320 }}>
          <span>Which studio?</span>
          <select
            className="inp"
            value={studio}
            onChange={(e) => setStudio(e.target.value)}
            data-testid="photo-studio-select"
          >
            <option value="balboa">Balboa Recording Studio</option>
            <option value="bungalow">Balboa Bungalow</option>
          </select>
        </label>
        <input
          ref={fileRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,image/heic,image/heif"
          multiple
          onChange={upload}
          disabled={busy}
          data-testid="photo-upload-input"
        />
        {busy && (
          <p className="mono" style={{ fontSize: 13, marginTop: 10 }} data-testid="photo-upload-progress">
            {progress
              ? `Uploading ${progress.index} of ${progress.total} — ${progress.pct}% (${progress.name})`
              : "Uploading…"}
          </p>
        )}
        {errors.length > 0 && (
          <div style={{ marginTop: 10 }} data-testid="photo-upload-errors">
            {errors.map((m, i) => (
              <p key={i} className="mono" style={{ fontSize: 13, color: "var(--orange)" }}>{m}</p>
            ))}
          </div>
        )}
        {err && (
          <p className="mono" style={{ fontSize: 13, color: "var(--orange)", marginTop: 10 }} data-testid="photo-error">
            {err}
          </p>
        )}
      </div>

      {photos.length ? (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(220px,1fr))", gap: 16 }}>
          {photos.map((p) => (
            <div
              key={p.id}
              className="card drag-item"
              draggable
              onDragStart={() => (dragId.current = p.id)}
              onDragOver={(e) => e.preventDefault()}
              onDrop={() => onDrop(p.id)}
              data-testid={`photo-card-${p.id}`}
            >
              <div className="photo-plain" style={{ aspectRatio: "4/3" }}>
                <img src={cld(p.url, 600)} alt="" loading="lazy" />
              </div>
              <div style={{ display: "flex", gap: 8, marginTop: 12, flexWrap: "wrap" }}>
                <button
                  className="btn btn-sm btn-ghost"
                  onClick={() => toggle(p)}
                  data-testid={`photo-toggle-${p.id}`}
                >
                  {p.visible ? "Visible" : "Hidden"}
                </button>
                <button
                  className="btn btn-sm btn-ghost"
                  onClick={() => setStudioFor(p)}
                  data-testid={`photo-studio-${p.id}`}
                >
                  {p.studio === "bungalow" ? "Bungalow" : "Balboa"}
                </button>
                <button className="btn btn-sm" onClick={() => remove(p)} data-testid={`photo-delete-${p.id}`}>
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="empty">— no photos yet, upload above —</div>
      )}
    </div>
  );
}
